// src/modules/accounting/sections.jsx
// Single source of truth for the accounting dashboard grid + per-section routes.
import React from 'react';
import {
  DashboardOutlined, BankOutlined, WalletOutlined, LineChartOutlined,
  BookOutlined, AccountBookOutlined, BarChartOutlined, PieChartOutlined,
  AuditOutlined, FileTextOutlined, ShopOutlined, GoldOutlined,
  NodeIndexOutlined,
} from '@ant-design/icons';
import StockRegisterPage from './pages/StockRegisterPage';
import GstCentrePage from './pages/GstCentrePage';

import AccountingDashboard from './pages/AccountingDashboard';
import DoubleEntryDashboard from './pages/DoubleEntryDashboard';
import FundContributionsPage from './pages/FundContributionsPage';
import ExpensesPage from './pages/ExpensesPage';
import BranchExpenseReportPage from './pages/BranchExpenseReportPage';
import CasePnlPage from './pages/CasePnlPage';
import TrialBalancePage from './pages/TrialBalancePage';
import ProfitLossPage from './pages/ProfitLossPage';
import BalanceSheetPage from './pages/BalanceSheetPage';
import GeneralLedgerPage from './pages/GeneralLedgerPage';
import JournalEntriesPage from './pages/JournalEntriesPage';
import ChartOfAccountsPage from './pages/ChartOfAccountsPage';
import PostingRulesPage from './pages/PostingRulesPage';

// `path` is relative to /accounting. `fieldName` drives permission visibility.
// `color` is the accent used for the card icon tile.
export const ACCOUNTING_SECTIONS = [
  {
    key: 'overview',
    path: 'overview',
    fieldName: 'dashboard',
    title: 'Overview',
    description: 'Financial KPIs, charts and a company-wide accounting snapshot.',
    icon: <DashboardOutlined />,
    color: '#1677ff',
    Component: AccountingDashboard,
  },
  {
    key: 'double-entry',
    path: 'double-entry',
    fieldName: 'doubleEntry',
    title: 'Double-Entry',
    description: 'Double-entry posting summary and health of the ledger.',
    icon: <AccountBookOutlined />,
    color: '#722ed1',
    Component: DoubleEntryDashboard,
  },
  {
    key: 'journal-entries',
    path: 'journal-entries',
    fieldName: 'journalEntries',
    title: 'Journal Entries',
    description: 'Browse, search and manage all posted journal entries.',
    icon: <AuditOutlined />,
    color: '#eb2f96',
    Component: JournalEntriesPage,
  },
  {
    key: 'chart-of-accounts',
    path: 'chart-of-accounts',
    fieldName: 'chartOfAccounts',
    title: 'Chart of Accounts',
    description: 'Maintain the ledger account hierarchy and account codes.',
    icon: <BookOutlined />,
    color: '#13c2c2',
    Component: ChartOfAccountsPage,
  },
  {
    key: 'posting-rules',
    path: 'posting-rules',
    fieldName: 'postingRules',
    title: 'Posting Rules',
    description: 'Choose which account every automatic journal entry hits — purchases, settlements, smelter, funds, expenses, GST and stock. Approval-gated.',
    icon: <NodeIndexOutlined />,
    color: '#597ef7',
    Component: PostingRulesPage,
  },
  {
    key: 'general-ledger',
    path: 'general-ledger',
    fieldName: 'generalLedger',
    title: 'General Ledger',
    description: 'Account-wise ledger of every debit and credit posted.',
    icon: <BookOutlined />,
    color: '#2f54eb',
    Component: GeneralLedgerPage,
  },
  {
    key: 'trial-balance',
    path: 'trial-balance',
    fieldName: 'trialBalance',
    title: 'Trial Balance',
    description: 'Verify total debits equal total credits across accounts.',
    icon: <FileTextOutlined />,
    color: '#fa8c16',
    Component: TrialBalancePage,
  },
  {
    key: 'profit-loss',
    path: 'profit-loss',
    fieldName: 'profitLoss',
    title: 'Profit & Loss',
    description: 'Income and expense statement for any period.',
    icon: <BarChartOutlined />,
    color: '#52c41a',
    Component: ProfitLossPage,
  },
  {
    key: 'balance-sheet',
    path: 'balance-sheet',
    fieldName: 'balanceSheet',
    title: 'Balance Sheet',
    description: 'Assets, liabilities and equity at a point in time.',
    icon: <PieChartOutlined />,
    color: '#f5222d',
    Component: BalanceSheetPage,
  },
  {
    key: 'funds',
    path: 'funds',
    fieldName: 'fundContributions',
    title: 'Fund Contributions',
    description: 'Track capital and fund contributions into the business.',
    icon: <BankOutlined />,
    color: '#faad14',
    Component: FundContributionsPage,
  },
  {
    key: 'expenses',
    path: 'expenses',
    fieldName: 'expenses',
    title: 'Expenses',
    description: 'Record and review operating expenses.',
    icon: <WalletOutlined />,
    color: '#a0d911',
    Component: ExpensesPage,
  },
  {
    key: 'branch-expenses',
    path: 'branch-expenses',
    fieldName: 'expenses',
    title: 'Branch-wise Expenses',
    description: 'Cumulative expense spend per branch — cash and bank combined, with manual entry from the branch drawer or a bank fund.',
    icon: <ShopOutlined />,
    color: '#1890ff',
    Component: BranchExpenseReportPage,
  },
  {
    key: 'case-pnl',
    path: 'case-pnl',
    fieldName: 'casePnl',
    title: 'Case P/L',
    description: 'Profit and loss analysis per gold-loan case.',
    icon: <LineChartOutlined />,
    color: '#08979c',
    Component: CasePnlPage,
  },
  {
    key: 'stock-register',
    path: 'stock-register',
    fieldName: 'stockRegister',
    title: 'Stock Register',
    description: 'Metal in/out in grams and value — every movement linked to its journal entry, with approval-gated manual entries.',
    icon: <GoldOutlined />,
    color: '#d4a017',
    Component: StockRegisterPage,
  },
  {
    key: 'gst-centre',
    path: 'gst-centre',
    fieldName: 'gstCentre',
    title: 'GST Centre',
    description: 'Output GST, input tax credit, manual entries and a GSTR-style monthly summary in one place.',
    icon: <AuditOutlined />,
    color: '#c41d7f',
    Component: GstCentrePage,
  },
];
